import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import axios, { AxiosError } from "axios";
import { FormData, SubmitStatus } from "../types/FormData";

const AccidentForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus | null>(null);

  // Get URL parameters and prefill form
  const getInitialFormData = (): FormData => {
    const urlParams = new URLSearchParams(window.location.search);

    return {
      ProductId: Number(urlParams.get("ProductId")) || 45,
      treatedBy: urlParams.get("treatedBy") || "YES",
      physicalInjury: urlParams.get("physicalInjury") || "YES",
      hasAttorney: urlParams.get("hasAttorney") || "NO",
      accidentFault: urlParams.get("accidentFault") || "NO",
      caseDescription: urlParams.get("caseDescription") || "",
      date: urlParams.get("date") || null,
      zip: Number(urlParams.get("zip")) || 0,
      state: urlParams.get("state") || "",
      userIp: urlParams.get("userIp") || "",
      firstName: urlParams.get("firstName") || "",
      lastName: urlParams.get("lastName") || "",
      email: urlParams.get("email") || "",
      phone: urlParams.get("phone") || "",
      Years: Number(urlParams.get("Years")) || -1,
      source: urlParams.get("source") || "",
      interestScale: urlParams.get("interestScale") || "SCALE3",
      apiId: urlParams.get("apiId") || "2C01DACAA77C11EABBAB42010A5C50F3",
      apiPassword: urlParams.get("apiPassword") || "bea8049",
      webSiteUrl:
        urlParams.get("webSiteUrl") ||
        "https://clients.caraccidentcounsel.com/s3/Spanish-Youtubehttps://clients.caraccidentcounsel.com/s3/tiktok.spanish",
      tcpa: urlParams.get("tcpa") || "YES",
      accidentType: urlParams.get("accidentType") || "CAR",
      tcpaText:
        urlParams.get("tcpaText") ||
        "By clicking the next button I give consent to receive emails, calls, SMS/text messages, and communications from Car Accident Counsel and its network of lawyers/advocates to any number or email address given by me.",
      price: urlParams.get("price") || "0",
      userAgent: navigator.userAgent,
      leadLanguage: urlParams.get("leadLanguage") || "English",
      isDequalified: urlParams.get("isDequalified") === "true" || false,
      dequalReason: urlParams.get("dequalReason") || "",
      trustedFormUrl: urlParams.get("trustedFormUrl") || "",
      jornayaLeadId: urlParams.get("jornayaLeadId") || "",
    };
  };

  const [formData, setFormData] = useState<FormData>(getInitialFormData());

  // Log URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.toString()) {
      console.log("URL Parameters detected:", Object.fromEntries(urlParams));
      console.log("Form prefilled with data:", formData);
    }
  }, []);

  // Get PST DateTime
  const getPSTDateTime = (): string => {
    const now = new Date();
    const pstDate = new Date(
      now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
    );
    return pstDate.toISOString();
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ): void => {
    const { name, value } = e.target;
    setFormData((prev: FormData) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // First API call to Phonexa
      await axios.post(
        "https://beta.caraccidentcounsel.com/api/phonexa",
        formData,
        {
          headers: {
            "content-type": "application/json",
          },
        }
      );

      // Add submission date to data for Zapier
      const dataWithDate = {
        ...formData,
        submissionDate: getPSTDateTime(),
      };

      // Second API call to Zapier
      await axios.post(
        "https://beta.caraccidentcounsel.com/api/zapier",
        dataWithDate,
        {
          headers: {
            "content-type": "application/json",
          },
        }
      );

      setSubmitStatus({
        type: "success",
        message: "Form submitted successfully! We will contact you shortly.",
      });

      console.log("Form submitted successfully to both APIs");

      // Optional: Reset form after successful submission
      // setFormData(initialFormData);
      // Or redirect to success page
      // window.location.href = '/success';
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      setSubmitStatus({
        type: "error",
        message:
          axiosError.response?.data?.message ||
          "Failed to submit form. Please try again.",
      });
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = (): void => {
    if (currentStep === 1 && !formData.caseDescription) {
      alert("Please tell us about your accident");
      return;
    }
    if (currentStep === 2 && !formData.date) {
      alert("Please select when the accident happened");
      return;
    }
    setCurrentStep((prev: number) => prev + 1);
  };

  const prevStep = (): void => {
    setCurrentStep((prev: number) => prev - 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">
              Accident Claim Form
            </h1>
            <div className="flex justify-center items-center space-x-2 mt-4">
              {[1, 2, 3].map((step: number) => (
                <div
                  key={step}
                  className={`h-2 rounded-full transition-all ${
                    step <= currentStep ? "bg-blue-600 w-12" : "bg-gray-300 w-8"
                  }`}
                />
              ))}
            </div>
            <p className="text-center text-sm text-gray-600 mt-2">
              Step {currentStep} of 3
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Step 1: Accident Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    1. Tell us about your accident{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="caseDescription"
                    value={formData.caseDescription}
                    onChange={handleChange}
                    rows={4}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Please describe what happened..."
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={nextStep}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Date */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    2. When did the accident happen?{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date || ""}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Personal Information */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-green-800 font-medium">
                    Thank you for your accident details. You may be due for a
                    major cash settlement!
                  </p>
                  <p className="text-green-700 text-sm mt-1">
                    Let's set you up with a free legal consultation with a local
                    attorney
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      3. First Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      4. Last Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    5. Your email <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    6. What is your cell number?{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="(555) 555-5555"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    For us to call you about compensation please be ready to
                    pick up
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    7. Zip code
                  </label>
                  <input
                    type="text"
                    name="zip"
                    value={formData.zip || ""}
                    onChange={handleChange}
                    maxLength={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    8. Source
                  </label>
                  <input
                    type="text"
                    name="source"
                    value={formData.source}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    9. Trusted Form URL
                  </label>
                  <input
                    type="url"
                    name="trustedFormUrl"
                    value={formData.trustedFormUrl}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    10. IP Address
                  </label>
                  <input
                    type="text"
                    name="userIp"
                    value={formData.userIp}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    11. YEARS (0-4)
                  </label>
                  <input
                    type="number"
                    name="Years"
                    value={formData.Years === -1 ? "" : formData.Years}
                    onChange={handleChange}
                    min={0}
                    max={4}
                    placeholder="Enter years (0-4)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    12. Language
                  </label>
                  <select
                    name="leadLanguage"
                    value={formData.leadLanguage}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="English">English</option>
                    <option value="Spanish">Spanish</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    13. Jornaya
                  </label>
                  <input
                    type="text"
                    name="jornayaLeadId"
                    value={formData.jornayaLeadId}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-xs text-gray-700">{formData.tcpaText}</p>
                </div>

                {submitStatus && (
                  <div
                    className={`p-4 rounded-lg ${
                      submitStatus.type === "success"
                        ? "bg-green-50 border border-green-200 text-green-800"
                        : "bg-red-50 border border-red-200 text-red-800"
                    }`}
                  >
                    {submitStatus.message}
                  </div>
                )}

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Form"}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AccidentForm;
